import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {searchForSongs} from "../../services/songs-db";
import './SearchComponent.css';
import {AppCtx} from "../../../../AppCtx";
import {useSelectSong} from "../../../navigation/components/Navigation";
import {FaFileAlt, FaFolderOpen, FaHome, FaSearch, FaTimes} from "react-icons/fa";
import DOMPurify from 'dompurify';
import { useNavigate, useLocation } from 'react-router-dom';
import {songPathUrl, songPathFromUrl} from '../../../../shared/services/songPathUtils';

export const SearchComponent = ({ tools }) => {
  const {setSearchResults, selectedFile, isFullScreen, isPresentationMode, isFileBrowserVisible} = useContext(AppCtx);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showTools, setShowTools] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const routePath = location.pathname === '/songs' || location.pathname.startsWith('/songs/')
    ? songPathFromUrl(location.pathname) : '';
  // Keep descendants when visiting an ancestor, until another branch is opened.
  const navigationPathRef = useRef(routePath || selectedFile.name || '');
  if (routePath && !navigationPathRef.current.startsWith(`${routePath}/`)) {
    navigationPathRef.current = routePath;
  }
  const currentPath = navigationPathRef.current;
  const parts = currentPath.split('/').filter(Boolean);
  const hasFile = /\.txt$/i.test(currentPath);
  const folders = hasFile ? parts.slice(0, -1) : parts;
  const pathScrollRef = useRef(null);
  useEffect(() => {
    const scroller = pathScrollRef.current;
    if (!scroller) return;
    // Initially keep the filename in view; scrolling left reveals the roots.
    const showCurrentPath = () => { scroller.scrollLeft = scroller.scrollWidth - scroller.clientWidth; };
    showCurrentPath();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(showCurrentPath);
    observer?.observe(scroller);
    window.addEventListener('resize', showCurrentPath);
    let active = true;
    document.fonts?.ready.then(() => { if (active) showCurrentPath(); });
    const scrollWithWheel = event => {
      if (scroller.scrollWidth <= scroller.clientWidth || event.ctrlKey || event.deltaX) return;
      event.preventDefault();
      scroller.scrollLeft += event.deltaY;
    };
    scroller.addEventListener('wheel', scrollWithWheel, {passive: false});
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener('resize', showCurrentPath);
      scroller.removeEventListener('wheel', scrollWithWheel);
    };
  }, [currentPath, isSearchFocused]);
  const searchStartedRef = useRef(false);
  const previousPathRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathRef.current !== location.pathname && location.pathname !== '/app/search') {
      setIsSearchFocused(false);
      setShowTools(false);
      setInputValue('');
    }
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (isFullScreen || isPresentationMode || isFileBrowserVisible) {
      setIsSearchFocused(false);
      setShowTools(false);
    }
  }, [isFullScreen, isPresentationMode, isFileBrowserVisible]);

  // Initialize input value from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    if (location.pathname === '/app/search' && query) {
      setInputValue(query);

      // Perform search with the URL query parameter directly
      const results = searchForSongs(query);
      setSearchResults(results);
    }
  }, [location, setSearchResults]);

  // Handle input changes and navigation
  const handleSearch = useCallback((value) => {
    if (value) {
      const results = searchForSongs(value);
      setSearchResults(results);

      const replace = searchStartedRef.current || location.pathname === '/app/search';
      searchStartedRef.current = true;
      navigate(`/app/search?q=${encodeURIComponent(value)}`, {replace});
    } else {
      // Clear results when query is empty
      setSearchResults([]);

      // If query is empty and we're on the search page, go back
      if (location.pathname === '/app/search') {
        if (window.history.state && window.history.state.idx > 0) {
          navigate(-1);
        } else {
          navigate('/');
        }
      }
    }
  }, [location.pathname, navigate, setSearchResults]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (newValue) setShowTools(false);
    handleSearch(newValue);
  };

  const onFileFocused = () => {
    searchStartedRef.current = location.pathname === '/app/search';
    async function fetchSongs() {
      searchForSongs('', true);
    }
    fetchSongs();
    setIsSearchFocused(true);
    setShowTools(true);
    inputRef.current?.focus();
  }

  const closeSearch = useCallback(() => {
    setInputValue('');
    handleSearch('');
    setIsSearchFocused(false);
    setShowTools(false);
    searchStartedRef.current = false;
  }, [handleSearch]);

  const searchContainerRef = useRef(null);
  useEffect(() => {
    if (!isSearchFocused) return;
    const cancelOutside = event => {
      const target = event.target;
      if (!(target instanceof Element) || searchContainerRef.current?.contains(target)) return;
      // Results navigate to a song; portaled dialogs belong to the open tools.
      if (target.closest('.search-result-card, .search-index-status, .search-results li, [role="dialog"], .ReactModal__Overlay')) return;
      closeSearch();
    };
    document.addEventListener('pointerdown', cancelOutside, true);
    return () => document.removeEventListener('pointerdown', cancelOutside, true);
  }, [isSearchFocused, closeSearch]);

  const inputRef = useRef(null);
  useEffect(() => {
    if (isSearchFocused) {
      inputRef.current?.focus();
    }
  }, [isSearchFocused]);

  const pathLinks = <span className="search-path" aria-label="Bieżąca ścieżka">
    {folders.map((folder, index) => <React.Fragment key={index}>
      <button type="button" className="search-trigger search-path-part"
        title={folder} aria-label={`Otwórz katalog ${folder}`}
        onClick={() => navigate(songPathUrl(folders.slice(0, index + 1).join('/')))}>{folder}</button>
      {(index < folders.length - 1 || hasFile) && <span className="search-path-separator" aria-hidden="true">/</span>}
    </React.Fragment>)}
    {hasFile && <button type="button" className="search-trigger search-path-part current-file-name"
      onClick={() => {
        if (location.pathname.startsWith('/songs/') && songPathFromUrl(location.pathname) === currentPath) {
          onFileFocused();
          return;
        }
        setIsSearchFocused(false);
        setShowTools(false);
        navigate(songPathUrl(currentPath));
      }} title={parts[parts.length - 1]}
      aria-label={`Otwórz śpiewnik ${parts[parts.length - 1]}`}>{parts[parts.length - 1]}</button>}
  </span>;

  return (
    <> {isSearchFocused ? null :
      <span className="search-path-bar" onClick={event => {
        if (!event.target.closest('button')) onFileFocused();
      }}>
        <button type="button" className="search-trigger search-icon-trigger" onClick={onFileFocused}
                aria-label="Szukaj piosenki" title="Szukaj piosenki"><FaSearch aria-hidden="true"/></button>
        <span ref={pathScrollRef} className="search-path-scroll">
          <span className="search-path-roots">
            <button type="button" className="search-trigger search-path-root" onClick={() => navigate('/app/dashboard')}
              aria-label="Przejdź do strony głównej" title="Przejdź do strony głównej"><FaHome aria-hidden="true"/></button>
            <span className="search-path-separator" aria-hidden="true">/</span>
            <button type="button" className="search-trigger search-path-root" onClick={() => navigate('/songs')}
              aria-label="Przejdź do katalogów" title="Przejdź do katalogów"><FaFolderOpen aria-hidden="true"/></button>
            {currentPath && <span className="search-path-separator" aria-hidden="true">/</span>}
          </span>
          {pathLinks}
        </span>
      </span>
    } {isSearchFocused && createPortal(
      <div ref={searchContainerRef} className="search-container">
        <button type="button" className="search-menu-toggle" onClick={() => setShowTools(value => !value)}
          aria-label={showTools ? 'Schowaj narzędzia' : 'Pokaż narzędzia'} aria-expanded={showTools} title={showTools ? 'Schowaj narzędzia' : 'Pokaż narzędzia'}>
          <FaSearch aria-hidden="true"/>
        </button>
        <input
          ref={inputRef}
          type="search"
          name="song-search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="search"
          enterKeyHint="search"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={event => { if (event.key === 'Escape') closeSearch(); }}
          placeholder="Szukaj..."
          className="search-input"
          aria-label="Szukaj piosenki"
        />
        <button type="button" className="search-close" onClick={closeSearch} aria-label="Zamknij wyszukiwanie" title="Zamknij wyszukiwanie">
          <FaTimes/>
        </button>
        {showTools && tools && <div className="search-tools-panel">{pathLinks}{tools}</div>}
      </div>
    , document.body)}
    </>
  );
};

export const SearchResults = () => {
  const {searchResults} = useContext(AppCtx);
  const selectSong = useSelectSong();

  const highlightTerms = (text, terms) => {
    if (!text || !terms || terms.length === 0) return text;
    const regex = new RegExp(`(${terms.join('|')})`, 'giu');
    return text && DOMPurify.sanitize(text.replace(regex, match => `<mark>${match}</mark>`));
  };

  const handleSongClick = (song) => {
    selectSong(song.fileName, song.songNumber);
  };

  return (
    <div className="search-results-wrapper">
      <ul className="search-results">
        {searchResults.map((result) => (
          <li key={result.id} onClick={() => handleSongClick(result)}>
            <div><FaFileAlt/><span className={"file-name"}> {result.fileName}</span></div>
            <p dangerouslySetInnerHTML={{__html: highlightTerms(result.content, result.terms)}}></p>
          </li>
        ))}
      </ul>
    </div>
  );
};
